import React from "react";

// A clean two-column (sidebar) resume layout.
// Consumes the normalized resumeData shape returned by /api/resume/redesign,
// PLUS a `design` object (also returned by that same endpoint) that carries
// AI-generated colors/fonts/spacing derived from the user's free-text
// description. Content always comes from resumeData; look & feel always
// comes from design.

const SIDEBAR_W = 260;

const DEFAULT_DESIGN = {
  primaryColor: "#002B5B",
  accentColor: "#0288D1",
  backgroundColor: "#ffffff",
  sidebarColor: "#002B5B",
  textColor: "#1f2937",
  mutedColor: "#6b7280",
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  headingCase: "uppercase",
  spacing: "normal",
};

const SPACING_SCALE = {
  compact: { sidebar: "28px 20px", main: "28px 28px", section: 16, entry: 10, gap: 8 },
  normal: { sidebar: "40px 26px", main: "40px 36px", section: 24, entry: 16, gap: 10 },
  spacious: { sidebar: "52px 32px", main: "52px 44px", section: 32, entry: 22, gap: 14 },
};

const dateRange = (start, end, current) =>
  [start, current ? "Present" : end].filter(Boolean).join(" – ");

// Rough "is this color light or dark" check so sidebar text stays readable
// even when the AI picks a light sidebar color.
function isLight(hex) {
  const h = (hex || "").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  if (full.length !== 6) return false;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 150;
}

export default function TwoColumnResumeTemplate({ data = {}, design = {}, photoUrl = "" }) {
  const d = { ...DEFAULT_DESIGN, ...design };
  const sp = SPACING_SCALE[d.spacing] || SPACING_SCALE.normal;
  const sidebarTextColor = isLight(d.sidebarColor) ? "#111827" : "#ffffff";
  const sidebarMutedColor = isLight(d.sidebarColor) ? "#374151" : "#e5edf7";
  const sidebarLabelColor = isLight(d.sidebarColor) ? d.primaryColor : "#ffffffcc";

  const styles = {
    page: {
      width: "794px",
      minHeight: "1123px",
      background: d.backgroundColor,
      margin: "0 auto",
      display: "flex",
      fontFamily: d.fontFamily,
      boxSizing: "border-box",
      overflow: "hidden",
    },
    sidebar: {
      width: SIDEBAR_W,
      flexShrink: 0,
      background: d.sidebarColor,
      color: sidebarTextColor,
      padding: sp.sidebar,
      boxSizing: "border-box",
    },
    avatar: {
      width: 92, height: 92, borderRadius: "50%", background: d.accentColor,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: 32, fontWeight: 800, color: "#fff", margin: "0 auto 18px",
    },
    sideName: { fontSize: 19, fontWeight: 800, textAlign: "center", margin: "0 0 2px", color: sidebarTextColor },
    sideRole: { fontSize: 12, textAlign: "center", color: sidebarLabelColor, marginBottom: 22, fontWeight: 600 },
    sideSection: { marginBottom: sp.section },
    sideTitle: {
      fontSize: 11.5, fontWeight: 800, letterSpacing: "1.5px",
      textTransform: d.headingCase === "uppercase" ? "uppercase" : "none",
      color: sidebarLabelColor, marginBottom: 10,
      borderBottom: `1px solid ${isLight(d.sidebarColor) ? "rgba(0,0,0,0.15)" : "rgba(255,255,255,0.25)"}`,
      paddingBottom: 6,
    },
    sideText: { fontSize: 12, lineHeight: 1.8, color: sidebarMutedColor, wordBreak: "break-word" },
    skillPill: {
      display: "inline-block", fontSize: 11, fontWeight: 600, color: sidebarTextColor,
      background: isLight(d.sidebarColor) ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.14)",
      borderRadius: 20, padding: "4px 12px", margin: `0 ${sp.gap}px ${sp.gap}px 0`,
    },
    main: { flex: 1, padding: sp.main, boxSizing: "border-box", color: d.textColor },
    sectionTitle: {
      fontSize: 13, fontWeight: 800, letterSpacing: "1.5px",
      textTransform: d.headingCase === "uppercase" ? "uppercase" : "none",
      color: d.primaryColor, margin: "0 0 12px", paddingBottom: 6, borderBottom: `2px solid ${d.primaryColor}`,
    },
    sectionBlock: { marginBottom: sp.section + 2 },
    para: { fontSize: 13, lineHeight: 1.75, color: d.textColor, margin: 0, whiteSpace: "pre-wrap" },
    entryHead: { display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 2 },
    entryTitle: { fontSize: 14, fontWeight: 700, color: d.textColor },
    entryMeta: { fontSize: 11.5, color: d.accentColor, fontWeight: 700 },
    entrySub: { fontSize: 12, color: d.mutedColor, marginBottom: 6, fontStyle: "italic" },
    entryBlock: { marginBottom: sp.entry },
  };

  const pd = data.personalDetails || {};
  const fullName = [pd.firstName, pd.lastName].filter(Boolean).join(" ") || "Your Name";
  const initials = ((pd.firstName?.[0] || "?") + (pd.lastName?.[0] || "")).toUpperCase();

  return (
    <div style={styles.page} id="redesigned-resume">
      {/* ── SIDEBAR ── */}
      <div style={styles.sidebar}>
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={fullName}
            style={{ width: 92, height: 92, borderRadius: "50%", objectFit: "cover", display: "block", margin: "0 auto 18px", border: `3px solid ${d.accentColor}` }}
          />
        ) : (
          <div style={styles.avatar}>{initials}</div>
        )}
        <div style={styles.sideName}>{fullName}</div>
        {pd.jobTitle && <div style={styles.sideRole}>{pd.jobTitle}</div>}

        <div style={styles.sideSection}>
          <div style={styles.sideTitle}>Contact</div>
          {pd.email && <div style={styles.sideText}>✉ {pd.email}</div>}
          {pd.phone && <div style={styles.sideText}>☎ {pd.phone}</div>}
          {pd.city && <div style={styles.sideText}>📍 {pd.city}</div>}
          {pd.linkedIn && <div style={styles.sideText}>🔗 {pd.linkedIn}</div>}
          {pd.website && <div style={styles.sideText}>🌐 {pd.website}</div>}
        </div>

        {Array.isArray(data.skills) && data.skills.length > 0 && (
          <div style={styles.sideSection}>
            <div style={styles.sideTitle}>Skills</div>
            <div>{data.skills.map((s, i) => <span key={i} style={styles.skillPill}>{s}</span>)}</div>
          </div>
        )}

        {Array.isArray(data.education) && data.education.length > 0 && (
          <div style={styles.sideSection}>
            <div style={styles.sideTitle}>Education</div>
            {data.education.map((e, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ ...styles.sideText, fontWeight: 700 }}>{e.degree}</div>
                <div style={styles.sideText}>{e.institution}</div>
                <div style={{ ...styles.sideText, opacity: 0.75 }}>{dateRange(e.startDate, e.endDate)}</div>
              </div>
            ))}
          </div>
        )}

        {Array.isArray(data.languages) && data.languages.length > 0 && (
          <div style={styles.sideSection}>
            <div style={styles.sideTitle}>Languages</div>
            <div>{data.languages.map((l, i) => <span key={i} style={styles.skillPill}>{l}</span>)}</div>
          </div>
        )}

        {Array.isArray(data.certifications) && data.certifications.length > 0 && (
          <div style={styles.sideSection}>
            <div style={styles.sideTitle}>Certifications</div>
            {data.certifications.map((c, i) => (
              <div key={i} style={{ marginBottom: 8 }}>
                <div style={{ ...styles.sideText, fontWeight: 700 }}>{c.name}</div>
                <div style={{ ...styles.sideText, opacity: 0.75 }}>{[c.provider, c.year].filter(Boolean).join(" · ")}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── MAIN ── */}
      <div style={styles.main}>
        {data.summary && (
          <div style={styles.sectionBlock}>
            <div style={styles.sectionTitle}>Profile</div>
            <p style={styles.para}>{data.summary}</p>
          </div>
        )}

        {Array.isArray(data.workExperience) && data.workExperience.length > 0 && (
          <div style={styles.sectionBlock}>
            <div style={styles.sectionTitle}>Work Experience</div>
            {data.workExperience.map((w, i) => (
              <div key={i} style={styles.entryBlock}>
                <div style={styles.entryHead}>
                  <span style={styles.entryTitle}>{w.jobTitle}</span>
                  <span style={styles.entryMeta}>{dateRange(w.startDate, w.endDate, w.current)}</span>
                </div>
                <div style={styles.entrySub}>{[w.employer, w.location].filter(Boolean).join(" · ")}</div>
                {w.description && <p style={styles.para}>{w.description}</p>}
              </div>
            ))}
          </div>
        )}

        {Array.isArray(data.projects) && data.projects.length > 0 && (
          <div style={styles.sectionBlock}>
            <div style={styles.sectionTitle}>Projects</div>
            {data.projects.map((p, i) => (
              <div key={i} style={styles.entryBlock}>
                <div style={styles.entryTitle}>{p.name}</div>
                {p.description && <p style={styles.para}>{p.description}</p>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}